/** French-first user messages for HTTP status; detail from JSON body when present. */

/** Thrown by admin `api` layer so callers can parse validation payloads. */
export class HttpError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly bodyText: string,
  ) {
    super(message);
    this.name = 'HttpError';
  }
}

const STATUS_FR: Record<number, string> = {
  400: 'Requête invalide.',
  401: 'Session expirée ou non autorisée. Reconnectez-vous.',
  403: 'Accès refusé.',
  404: 'Élément introuvable.',
  409: 'Conflit : les données ont peut-être changé.',
  413: 'Fichier trop volumineux.',
  422: 'Données invalides.',
  429: 'Trop de requêtes. Réessayez dans un instant.',
  500: 'Erreur serveur. Réessayez plus tard.',
  502: 'Service indisponible.',
  503: 'Service temporairement indisponible.',
};

/** Prefer user-facing strings from Nest ConflictException / BadRequest object bodies. */
function messageFromUnknownPayload(value: unknown): string {
  if (typeof value === 'string') return value;
  if (!value || typeof value !== 'object') return '';
  const row = value as Record<string, unknown>;
  for (const key of ['code', 'message', 'error', 'detail'] as const) {
    const v = row[key];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  for (const v of Object.values(row)) {
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
}

const NEST_GENERIC_STATUS_MESSAGES = new Set([
  'conflict',
  'bad request',
  'unauthorized',
  'forbidden',
  'not found',
  'unprocessable entity',
  'internal server error',
  'payload too large',
  'too many requests',
]);

function extractMessageFromBody(text: string): string {
  const trimmed = text.trim();
  if (!trimmed) return '';
  if (trimmed.startsWith('<')) return '';
  try {
    const j = JSON.parse(trimmed) as { message?: unknown; error?: string; code?: unknown };
    const topCode = typeof j.code === 'string' && j.code.trim() ? j.code.trim() : '';

    if (typeof j.message === 'string') {
      const msg = j.message.trim();
      // Nest ConflictException({ code: '…' }) often yields message: "Conflict" + top-level code.
      if (topCode && NEST_GENERIC_STATUS_MESSAGES.has(msg.toLowerCase())) return topCode;
      if (msg) return msg;
    }
    if (Array.isArray(j.message)) {
      const parts: string[] = [];
      for (const item of j.message) {
        if (typeof item === 'string') {
          parts.push(item);
          continue;
        }
        if (item && typeof item === 'object' && 'constraints' in item) {
          const row = item as { constraints?: Record<string, string> };
          const first =
            row.constraints && typeof row.constraints === 'object' ? Object.values(row.constraints)[0] : undefined;
          if (first) parts.push(first);
        } else {
          const nested = messageFromUnknownPayload(item);
          if (nested) parts.push(nested);
        }
      }
      if (parts.length) return parts.join(' ');
    }
    const fromMessageObj = messageFromUnknownPayload(j.message);
    if (fromMessageObj) return fromMessageObj;
    if (topCode) return topCode;
    if (typeof j.error === 'string' && !NEST_GENERIC_STATUS_MESSAGES.has(j.error.toLowerCase())) {
      return j.error;
    }
  } catch {
    /* plain text */
  }
  return trimmed.length > 280 ? `${trimmed.slice(0, 280)}…` : trimmed;
}

const LOGIN_CREDENTIAL_MESSAGES = new Set([
  'invalid email or password',
  'email ou mot de passe invalide',
]);

/** Build a single French-friendly line for failed responses. */
export function messageFromFailedResponse(status: number, bodyText: string): string {
  const base = STATUS_FR[status] ?? `Erreur ${status}`;
  const detail = extractMessageFromBody(bodyText);
  if (!detail || detail === base) return base;
  if (
    status === 401 &&
    LOGIN_CREDENTIAL_MESSAGES.has(detail.trim().toLowerCase())
  ) {
    return 'E-mail ou mot de passe incorrect.';
  }
  if (/^[A-Za-z\s]+$/.test(detail) && !/[àâéèêëïîôùûüÿçœæ]/.test(detail)) return base;
  if (detail.length > 120 || /prisma|invocation|\.ts:|\.js:|stack trace/i.test(detail)) return base;
  // Prefer short product French over generic status (esp. 409 code collisions).
  if ((status === 409 || status === 400) && detail.length <= 120) {
    if (status === 409) return detail;
    // 400 with product French (e.g. e-mail déjà utilisé) — drop "Requête invalide. (…)" wrapper.
    if (/[àâéèêëïîôùûüÿçœæ]/i.test(detail) || /déjà|requis|invalide|utilisé/i.test(detail)) {
      return detail;
    }
  }
  return `${base} (${detail})`;
}

/** Normalize any thrown value to a display string (after api throws Error). */
export function getDisplayError(error: unknown): string {
  if (error instanceof Error) return error.message;
  return String(error);
}

/**
 * Map Nest ValidationPipe / class-validator payloads to field keys (snake_case from API → same keys).
 * Returns `null` if body is not a structured validation error.
 */
export function parseFieldErrorsFromHttpError(e: unknown): Record<string, string> | null {
  if (!(e instanceof HttpError)) return null;
  if (e.status !== 400 && e.status !== 422) return null;
  const trimmed = e.bodyText.trim();
  if (!trimmed.startsWith('{')) return null;
  try {
    const j = JSON.parse(trimmed) as { message?: unknown };
    if (j.message == null) return null;
    const out: Record<string, string> = {};
    if (Array.isArray(j.message)) {
      for (const item of j.message) {
        if (typeof item === 'string') {
          out._form = (out._form ? `${out._form} ` : '') + item;
          continue;
        }
        if (item && typeof item === 'object' && 'property' in item) {
          const row = item as { property?: string; constraints?: Record<string, string> };
          const prop = row.property ?? '';
          const first =
            row.constraints && typeof row.constraints === 'object'
              ? Object.values(row.constraints)[0]
              : undefined;
          if (prop && first) out[prop] = first;
        }
      }
    } else if (typeof j.message === 'string') {
      out._form = j.message;
    }
    return Object.keys(out).length ? out : null;
  } catch {
    return null;
  }
}
